// Tab shell — currently 6 tabs (總覽/攝影機/事件/回顧/即時/帳號), equal weight, no
// centre-emphasis. Left rail ≥768px, fixed bottom tab bar below (global.css .app-nav).
// ⚠ DIVERGENCE: specs/16_client_ia_navigation.md §3 + apps/home/specs/ia.md §0.2/§3 mandate a
// 4-tab skeleton ("不新增第五 Tab") with the dual events⇄devices axis realized via the
// landing page, not extra tabs. The 總覽/回顧 additions here exceed that and are pending
// reconciliation (design review rank 6 — make 總覽 the index, fold 回顧 into Events).
//
// Events tab 未讀紅點（16 §2.2，W3/APP-01）：來源 = WSS room 廣播（web v1 無
// push，16 §10）。不在 /events 時收到即時事件 → bump；進入 /events 即清零。
// 已讀狀態 v1 本地（16 §5.3）。
// WEB3-F3：room 訂閱 tab 無關——連線由 AuthContext 隨登入建立，但停留 /account
// 之類非裝置頁時若無人宣告 rooms，badge 仍收不到廣播。shell 掛載（= 已登入，
// RequireAuth 之內）即拉一次裝置清單宣告全部 rooms（16 §5.2/§2.2）；liveEvents
// registry 負責維持與重連重訂（冪等），裝置頁既有 ensureEventRooms 呼叫不變。
//
// 連線狀態 banner（W2-02，spec 10 §4.3/§7/AC-OS-8）：degraded（WSS 斷、REST
// 正常）→ 黃 banner；network down → 灰 banner。banner 全域唯一落點在這。
// Live tab 本身保持可導航（hub 是 REST 清單頁，degraded 時其「觀看」入口
// 各自 disable——spec 的「即時功能 disable」指 start_viewing 類動作入口）。

import { brandDisplayName, brandLogoSrc } from '../lib/brand';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { ensureEventRooms, onLiveEvent, unreadEvents } from '../features/events/liveEvents';
import { doorbellRing, isDoorbellEvent } from '../features/doorbell/doorbellRing';
import { IncomingDoorbellCard } from '../features/doorbell/IncomingDoorbellCard';
import { isSnoozed } from '../features/devices/snooze';
import { apiFetch } from '../lib/api';
import { ConnectionBanner } from './ConnectionBanner';
import { NavIcon, type NavIconName } from './NavIcon';

// 事件 ⇄ 裝置「並重」雙主軸（apps/home/specs/ia.md §0，2026-06-17）：總覽 landing 對等
// 呈現兩軸；事件與攝影機在導覽中比鄰、等權（事件不再次於裝置）。
const TABS: Array<{ to: string; labelKey: string; icon: NavIconName }> = [
  { to: '/home', labelKey: 'nav.home', icon: 'home' },
  { to: '/devices', labelKey: 'nav.devices', icon: 'devices' },
  { to: '/events', labelKey: 'nav.events', icon: 'events' },
  { to: '/memories', labelKey: 'nav.memories', icon: 'memories' },
  { to: '/live', labelKey: 'nav.live', icon: 'live' },
  { to: '/account/subscription', labelKey: 'nav.subscription', icon: 'subscription' },
  { to: '/account', labelKey: 'nav.account', icon: 'account' },
];

export function AppShell() {
  const { t } = useTranslation();
  const unread = useSyncExternalStore(unreadEvents.subscribe, unreadEvents.get);
  const { pathname } = useLocation();
  const onEventsTab = pathname.startsWith('/events');
  const onEventsRef = useRef(onEventsTab);
  onEventsRef.current = onEventsTab;

  // deviceId → display_name（門鈴卡顯示用;同一份 /v3/devices 拉取所得,見下）。
  const deviceNamesRef = useRef<Map<string, string>>(new Map());
  // 目前所在的即時頁 deviceId（在看哪台就不對它彈門鈴卡——已經在現場）。
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  // 全域計數 + 門鈴來電卡：人在 Events tab 上時事件直接呈現於時間軸,不計未讀;
  // 門鈴(0x8030)則不論在哪個 tab 都全螢幕彈卡（來電語意,16 §2.2),除非正在看
  // 該台即時影像。
  useEffect(() => {
    return onLiveEvent((e) => {
      // Snooze（APP#19）：該裝置在靜音窗內 → 抑制**本機顯示**（門鈴來電卡 +
      // 未讀紅點 bump）。事件仍經 EventTimeline 正常入軸/入庫（只是靜默）。
      // 注意：這只抑制本機顯示;後端推播仍會送達（伺服端推播閘是 Scott follow-up）。
      if (isSnoozed(e.deviceId)) return;
      if (isDoorbellEvent(e.ev)) {
        const onItsLive = pathnameRef.current === `/devices/${e.deviceId}/live`;
        if (!onItsLive) {
          doorbellRing.ring(e.deviceId, deviceNamesRef.current.get(e.deviceId) ?? e.deviceId);
        }
      }
      if (!onEventsRef.current) unreadEvents.bump();
    });
  }, []);

  // WEB3-F3：登入即訂閱全部裝置 rooms（tab 無關；16 §5.2）。失敗靜默——badge
  // 是 best-effort 增強，devices/events 頁各自有權威載入與錯誤 UI + 重試，
  // 且其 ensureEventRooms 呼叫會在進頁時補上訂閱。
  useEffect(() => {
    let alive = true;
    apiFetch<Array<{ device_id: string; display_name?: string }>>('/v3/devices')
      .then((res) => {
        if (!alive) return;
        const devices = res.data ?? [];
        for (const d of devices) {
          if (d.display_name) deviceNamesRef.current.set(d.device_id, d.display_name);
        }
        ensureEventRooms(devices.map((d) => d.device_id));
      })
      .catch(() => {
        /* 靜默：見上 */
      });
    return () => {
      alive = false;
    };
  }, []);

  // 進入 Events tab → 視為已讀（紅點清零；含停留期間新事件）。
  useEffect(() => {
    if (onEventsTab) unreadEvents.clear();
  }, [onEventsTab, unread]);

  return (
    <div className="app-shell">
      <nav className="app-nav" aria-label={t('shell.nav.ariaLabel')}>
        <div className="app-nav-brand" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={brandLogoSrc()} alt="" width={24} height={24} style={{ borderRadius: 6, objectFit: 'contain' }} />
          <span>{brandDisplayName()}</span>
        </div>
        {TABS.map((tab) => (
          <NavLink key={tab.to} to={tab.to}>
            <NavIcon name={tab.icon} />
            {t(tab.labelKey)}
            {tab.to === '/events' && unread > 0 && (
              <span className="nav-badge" role="status" aria-label={t('shell.nav.unreadEvents', { count: unread })}>
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <main className="app-main">
        <ConnectionBanner />
        <Outlet />
      </main>
      <IncomingDoorbellCard />
    </div>
  );
}
